import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, CreditCard, X, Save, RefreshCw, Loader2, Landmark, Smartphone, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface PaymentMethod {
    id: number;
    method_name: string;
    method_type: "M-PESA" | "Bank Account";
    details: {
        paybill?: string;
        bank_name?: string;
        account_number?: string;
    };
    is_default: boolean;
}

export const ManagePaymentMethods: React.FC = () => {
    const navigate = useNavigate();
    const { warning, success, error: toastError } = useToast();
    const [methods, setMethods] = useState<PaymentMethod[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form Expansion state
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [methodType, setMethodType] = useState<"M-PESA" | "Bank Account">("Bank Account");
    const [methodName, setMethodName] = useState("");
    const [paybill, setPaybill] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [bankName, setBankName] = useState("");

    const fetchMethods = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.get("/payment-methods");
            setMethods(response.data);
        } catch (err: any) {
            console.error("Failed to load payment methods", err);
            setError(err.response?.data?.error || "Failed to load payment methods.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMethods();
    }, [fetchMethods]);

    const toggleForm = () => {
        if (!isFormOpen) {
            setMethodName("");
            setPaybill("");
            setAccountNumber("");
            setBankName("");
            setMethodType("Bank Account");
        }
        setIsFormOpen(!isFormOpen);
    };

    const popularBanks = [
        "Equity Bank",
        "KCB Bank",
        "Co-operative Bank",
        "Absa Bank",
        "NCBA Bank",
        "Stanbic Bank",
        "Standard Chartered",
        "I&M Bank",
        "Diamond Trust Bank (DTB)",
        "Family Bank",
        "Safaricom M-PESA (till/paybill via Bank)"
    ];

    const handleAddMethod = async () => {
        if (!methodName) {
            warning('Missing nickname', "Please give this payment method a nickname (e.g., 'My KCB Account').");
            return;
        }

        let details = {};
        if (methodType === "M-PESA") {
            if (!paybill) {
                warning('Missing paybill', 'Please enter the M-PESA Paybill or Till Number.');
                return;
            }
            details = { paybill, account_name: methodName };
        } else {
            if (!accountNumber || !bankName) {
                warning('Missing bank details', 'Please enter the Bank Name and Account Number.');
                return;
            }
            details = { bank_name: bankName, account_number: accountNumber };
        }

        setIsSubmitting(true);
        try {
            const payload = {
                methodName,
                methodType,
                details,
                isDefault: false
            };
            await apiClient.post("/payment-methods", payload);
            setIsFormOpen(false);
            fetchMethods();
        } catch (err: any) {
            console.error(err);
            toastError('Failed to add method', err.response?.data?.error || 'An error occurred.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputClass = "w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/50 font-medium transition-all";
    const labelClass = "block text-sm font-bold text-light-text dark:text-dark-text mb-2";

    return (
        <div className="max-w-4xl mx-auto pb-24 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between bg-light-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                    >
                        <ArrowLeft size={24} className="text-light-subtle dark:text-dark-subtle" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">Payout Methods</h1>
                        <p className="text-sm font-medium text-light-subtle dark:text-dark-subtle mt-0.5">Manage where you receive tenant payments</p>
                    </div>
                </div>
                <button
                    onClick={toggleForm}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold transition-all shadow-sm ${
                        isFormOpen 
                            ? 'bg-light-bg dark:bg-dark-bg text-light-subtle dark:text-dark-subtle border border-light-border dark:border-dark-border hover:opacity-80' 
                            : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                >
                    {isFormOpen ? <X size={20} /> : <Plus size={20} />}
                    <span className="hidden sm:inline">{isFormOpen ? 'Close' : 'Add Manual Method'}</span>
                </button>
            </div>

            {/* Tuma Integration Banner */}
            <div 
                onClick={() => navigate('/landlord/financials/tuma-setup')}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-900/80 dark:to-indigo-900/80 p-6 rounded-2xl shadow-md cursor-pointer hover:shadow-lg transition-all hover:scale-[1.01] group relative overflow-hidden"
            >
                <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10 transition-transform group-hover:scale-110" />
                <div className="flex items-center justify-between relative z-10">
                    <div className="flex items-start gap-4">
                        <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm shadow-inner">
                            <Zap className="text-white" size={28} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white mb-1">Automate with Tuma</h2>
                            <p className="text-blue-100 text-sm font-medium max-w-lg">
                                Generate a secure M-Pesa integration powered by Tuma. Invoices will automatically switch from Unpaid to Paid instantly!
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Inline Expanding Form */}
            <AnimatePresence>
                {isFormOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0, y: -10 }}
                        animate={{ opacity: 1, height: 'auto', y: 0 }}
                        exit={{ opacity: 0, height: 0, scale: 0.98 }}
                        className="overflow-hidden"
                    >
                        <div className="bg-light-card dark:bg-dark-card p-6 rounded-2xl shadow-md border-2 border-primary/20 space-y-8 mt-2">
                            <h2 className="text-xl font-bold text-light-text dark:text-dark-text flex items-center gap-2 border-b border-light-border dark:border-dark-border pb-4">
                                <CreditCard className="text-primary" size={24} /> Add New Collection Method
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div>
                                        <label className={labelClass}>Nickname</label>
                                        <input
                                            type="text"
                                            placeholder="e.g., KCB Main Business"
                                            value={methodName}
                                            onChange={(e) => setMethodName(e.target.value)}
                                            className={inputClass}
                                        />
                                    </div>

                                    <div>
                                        <label className={labelClass}>Method Type</label>
                                        <div className="flex rounded-xl overflow-hidden border border-light-border dark:border-dark-border bg-light-input dark:bg-dark-input p-1.5 gap-1 shadow-inner">
                                            <button
                                                onClick={() => setMethodType("M-PESA")}
                                                className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 rounded-lg transition-all ${
                                                    methodType === "M-PESA" 
                                                        ? "bg-primary text-white shadow-md" 
                                                        : "text-light-subtle dark:text-dark-subtle hover:bg-light-card dark:hover:bg-dark-card"
                                                }`}
                                            >
                                                <Smartphone size={18} /> M-PESA
                                            </button>
                                            <button
                                                onClick={() => setMethodType("Bank Account")}
                                                className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 rounded-lg transition-all ${
                                                    methodType === "Bank Account" 
                                                        ? "bg-primary text-white shadow-md" 
                                                        : "text-light-subtle dark:text-dark-subtle hover:bg-light-card dark:hover:bg-dark-card"
                                                }`}
                                            >
                                                <Landmark size={18} /> Bank Account
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    {methodType === "M-PESA" ? (
                                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                                            <div>
                                                <label className={labelClass}>Paybill / Till Number</label>
                                                <input
                                                    type="number"
                                                    placeholder="Enter verified number"
                                                    value={paybill}
                                                    onChange={(e) => setPaybill(e.target.value)}
                                                    className={inputClass}
                                                />
                                            </div>
                                        </motion.div>
                                    ) : (
                                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                                            <div>
                                                <label className={labelClass}>Bank Name</label>
                                                <div className="relative">
                                                    <select
                                                        value={bankName}
                                                        onChange={(e) => setBankName(e.target.value)}
                                                        className={`${inputClass} appearance-none cursor-pointer`}
                                                    >
                                                        <option value="" disabled>Select a Bank...</option>
                                                        {popularBanks.map((bank) => (
                                                            <option key={bank} value={bank}>{bank}</option>
                                                        ))}
                                                    </select>
                                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-light-subtle dark:text-dark-subtle">
                                                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <label className={labelClass}>Account Number</label>
                                                <input
                                                    type="number"
                                                    placeholder="Enter full account number"
                                                    value={accountNumber}
                                                    onChange={(e) => setAccountNumber(e.target.value)}
                                                    className={inputClass}
                                                />
                                            </div>
                                        </motion.div>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end pt-4 border-t border-light-border dark:border-dark-border">
                                <button
                                    onClick={handleAddMethod}
                                    disabled={isSubmitting}
                                    className="flex items-center justify-center gap-2 px-8 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 disabled:opacity-50 transition-all shadow-md min-w-[200px]"
                                >
                                    {isSubmitting ? (
                                        <><Loader2 className="animate-spin" size={20} /> Adding...</>
                                    ) : (
                                        <><Save size={20} /> Save Collection Method</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* List Content */}
            {loading ? (
                <div className="flex h-[30vh] items-center justify-center">
                    <Loader2 className="w-10 h-10 text-primary animate-spin" />
                </div>
            ) : error ? (
                <div className="bg-danger/10 p-6 rounded-2xl border border-danger/20 text-center space-y-4">
                    <p className="text-danger dark:text-red-400 font-bold">{error}</p>
                    <button onClick={fetchMethods} className="flex items-center justify-center gap-2 mx-auto text-primary font-bold hover:underline">
                        <RefreshCw size={18} /> Try Again
                    </button>
                </div>
            ) : methods.length === 0 ? (
                <div className="text-center py-24 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-2xl shadow-sm">
                    <CreditCard size={56} className="mx-auto text-light-subtle dark:text-dark-subtle mb-4 opacity-50" />
                    <p className="text-xl text-light-text dark:text-dark-text font-bold">No payout methods added yet</p>
                    <p className="text-sm font-medium text-light-subtle dark:text-dark-subtle mt-2">Tap 'Add Method' above to specify where you want to receive payments.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {methods.map(item => (
                        <div key={item.id} className="bg-light-card dark:bg-dark-card p-6 rounded-2xl shadow-sm border border-light-border dark:border-dark-border flex flex-col justify-between hover:shadow-md transition-shadow group">
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-xl font-bold text-light-text dark:text-dark-text flex items-center gap-3">
                                        {item.method_type === "M-PESA" ? <Smartphone className="text-primary" size={24} /> : <Landmark className="text-primary" size={24} />}
                                        {item.method_name}
                                    </h3>
                                    {item.is_default && (
                                        <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                                            Default
                                        </span>
                                    )}
                                </div>
                                
                                <div className="space-y-2 bg-light-bg dark:bg-dark-bg p-4 rounded-xl border border-light-border dark:border-dark-border">
                                    {item.method_type === "M-PESA" ? (
                                        <p className="flex items-center justify-between">
                                            <span className="text-sm font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Paybill/Till</span>
                                            <span className="font-bold text-light-text dark:text-dark-text text-lg">{item.details.paybill}</span>
                                        </p>
                                    ) : (
                                        <>
                                            <p className="flex items-center justify-between border-b border-light-border dark:border-dark-border pb-2">
                                                <span className="text-sm font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Bank</span>
                                                <span className="font-bold text-light-text dark:text-dark-text">{item.details.bank_name}</span>
                                            </p>
                                            <p className="flex items-center justify-between pt-2">
                                                <span className="text-sm font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Account</span>
                                                <span className="font-bold text-light-text dark:text-dark-text font-mono text-lg">•••• {item.details.account_number?.slice(-4)}</span>
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
